import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

export type PaymentResponseVariant = 'success' | 'failure';

/**
 * Payment result dialog (success or failure) — ticket shape styled via SCSS.
 */
@Component({
  selector: 'stupa-payment-response-popup',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './payment-response-popup.component.html',
  styleUrl: './payment-response-popup.component.scss',
})
export class PaymentResponsePopupComponent {
  @Input() visible = false;
  /** `failure` shows red X, reason/next-steps blocks, and Back + Retry. */
  @Input() variant: PaymentResponseVariant = 'success';
  @Input() transactionId = '';
  @Input() eventTitle = '';
  @Input() detailLines: string[] = [];
  /** Leave empty to use defaults for the current `variant`. */
  @Input() titleKey = '';
  @Input() subtitleKey = '';

  /**
   * Optional override for failure reason bullets (plain text). If empty, default i18n keys are used.
   */
  @Input() failureReasonLines: string[] = [];
  /**
   * Optional override for “next steps” bullets before the support line. If empty, default i18n keys are used.
   */
  @Input() failureNextStepLines: string[] = [];

  /** Link target for “contact” in the support line (failure only). */
  @Input() supportContactHref = '/';

  @Input() footerPrice = '';
  @Input() footerClosesText = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() viewInvoice = new EventEmitter<void>();
  @Output() viewOrderHistory = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() retryPayment = new EventEmitter<void>();

  readonly failureReasonI18nKeys = ['payment_failure_reason_line_1', 'payment_failure_reason_line_2'] as const;
  readonly failureNextStepI18nKeys = ['payment_failure_next_line_1', 'payment_failure_next_line_2'] as const;

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onBackdropClick(event: Event): void {
    if ((event.target as HTMLElement).classList.contains('payment-response-overlay')) {
      this.close();
    }
  }

  onViewInvoice(): void {
    this.viewInvoice.emit();
  }

  onViewOrderHistory(): void {
    this.viewOrderHistory.emit();
  }

  onBack(): void {
    this.back.emit();
    this.close();
  }

  onRetryPayment(): void {
    this.retryPayment.emit();
    this.close();
  }

  get isFailure(): boolean {
    return this.variant === 'failure';
  }

  get effectiveTitleKey(): string {
    return this.titleKey || (this.isFailure ? 'payment_failed' : 'payment_successful');
  }

  get effectiveSubtitleKey(): string {
    return this.subtitleKey || (this.isFailure ? 'payment_could_not_be_processed' : 'payment_received_successfully');
  }

  get useI18nReason(): boolean {
    return this.failureReasonLines.length === 0;
  }

  get useI18nNextSteps(): boolean {
    return this.failureNextStepLines.length === 0;
  }

  trackById(index: number, item: unknown): string | number {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) return id;
    }
    return index;
  }
}
